import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

export type ExtensionKind = 'person' | 'department'

/** A location row (``org_locations``). One main PSTN number per location; its
 *  extensions fan out via DTMF. */
export interface OrgLocation {
  id: string
  org_id: string
  label: string
  address: string | null
  main_phone: string
  created_at: number
  updated_at: number
}

/** An extension row (``org_extensions``) joined with its location for display.
 *  ``location_label`` + ``location_main_phone`` come from the LEFT JOIN in
 *  ``database.py::list_directory`` / ``get_extension``. */
export interface OrgExtension {
  id: string
  org_id: string
  location_id: string
  extension: string
  label: string
  kind: ExtensionKind
  /** Optional link to an app user → powers the follow-up "assigned to me". */
  user_id: string | null
  /** Optional DTMF override; default derived from ``extension``. */
  send_digits: string | null
  /** Optional free text the receptionist uses to disambiguate / confirm. */
  description: string | null
  created_at: number
  updated_at: number
  // JOINed location display
  location_label: string | null
  location_main_phone: string | null
}

/** ``GET /directory`` — full org directory for the admin page and the
 *  follow-up reassign picker. Returns locations (so the admin page can render a
 *  location with zero extensions) and the flat extension list. */
export interface DirectoryResponse {
  locations: OrgLocation[]
  extensions: OrgExtension[]
}

export interface LocationPayload {
  label?: string
  address?: string | null
  main_phone?: string
}

export interface ExtensionPayload {
  location_id: string
  extension: string
  label?: string
  kind?: ExtensionKind
  user_id?: string | null
  send_digits?: string | null
  description?: string | null
}

export interface ExtensionUpdatePayload {
  extension?: string
  label?: string
  kind?: ExtensionKind
  user_id?: string | null
  send_digits?: string | null
  description?: string | null
}

export function useDirectory() {
  return useQuery({
    queryKey: ['directory'],
    queryFn: () => apiFetch<DirectoryResponse>('/directory'),
  })
}

// ── Locations ───────────────────────────────────────────────────────────

export function useCreateLocation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: LocationPayload) =>
      apiFetch<{ location: OrgLocation }>('/directory/locations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directory'] })
    },
  })
}

export function useUpdateLocation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: LocationPayload & { id: string }) =>
      apiFetch<{ location: OrgLocation }>(`/directory/locations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directory'] })
    },
  })
}

export function useDeleteLocation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean; deleted: string }>(`/directory/locations/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directory'] })
    },
  })
}

// ── Extensions ──────────────────────────────────────────────────────────

export function useCreateExtension() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ExtensionPayload) =>
      apiFetch<{ extension: OrgExtension }>('/directory/extensions', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directory'] })
    },
  })
}

export function useUpdateExtension() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: ExtensionUpdatePayload & { id: string }) =>
      apiFetch<{ extension: OrgExtension }>(`/directory/extensions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directory'] })
    },
  })
}

export function useDeleteExtension() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean; deleted: string }>(`/directory/extensions/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directory'] })
    },
  })
}

/** ``POST /directory/import`` — parse a pasted block (location header +
 *  ``NNN Label`` lines) into rows. Returns a count of what was created. */
export interface ImportResult {
  created_locations: number
  created_extensions: number
}

export function useImportDirectory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (text: string) =>
      apiFetch<ImportResult>('/directory/import', {
        method: 'POST',
        body: JSON.stringify({ text }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directory'] })
    },
  })
}
